import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Lock, User, UserPlus, Loader2, AlertCircle, Globe, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';
import { LANGUAGE_FLAGS, LANGUAGE_NAMES } from '@/i18n/translations';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [emailExists, setEmailExists] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    dj_name: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  // Validation messages per language
  const validationMessages = {
    fr: {
      dj_name_required: 'Le nom de DJ est requis',
      dj_name_min: 'Minimum 2 caractères',
      email_required: 'L\'email est requis',
      email_invalid: 'Email invalide',
      password_required: 'Le mot de passe est requis',
      password_min: 'Minimum 8 caractères',
      email_exists: 'Un compte existe déjà avec cet email',
      error_generic: 'Erreur lors de la création du compte. Réessayez.'
    },
    en: {
      dj_name_required: 'DJ name is required',
      dj_name_min: 'Minimum 2 characters',
      email_required: 'Email is required',
      email_invalid: 'Invalid email',
      password_required: 'Password is required',
      password_min: 'Minimum 8 characters',
      email_exists: 'An account already exists with this email',
      error_generic: 'Error creating account. Please try again.'
    },
    es: {
      dj_name_required: 'El nombre de DJ es requerido',
      dj_name_min: 'Mínimo 2 caracteres',
      email_required: 'El email es requerido',
      email_invalid: 'Email inválido',
      password_required: 'La contraseña es requerida',
      password_min: 'Mínimo 8 caracteres',
      email_exists: 'Ya existe una cuenta con este email',
      error_generic: 'Error al crear la cuenta. Inténtalo de nuevo.'
    },
    de: {
      dj_name_required: 'DJ-Name ist erforderlich',
      dj_name_min: 'Mindestens 2 Zeichen',
      email_required: 'E-Mail ist erforderlich',
      email_invalid: 'Ungültige E-Mail',
      password_required: 'Passwort ist erforderlich',
      password_min: 'Mindestens 8 Zeichen',
      email_exists: 'Ein Konto mit dieser E-Mail existiert bereits',
      error_generic: 'Fehler beim Erstellen des Kontos. Bitte erneut versuchen.'
    },
    pt: {
      dj_name_required: 'Nome de DJ é obrigatório',
      dj_name_min: 'Mínimo 2 caracteres',
      email_required: 'Email é obrigatório',
      email_invalid: 'Email inválido',
      password_required: 'Senha é obrigatória',
      password_min: 'Mínimo 8 caracteres',
      email_exists: 'Já existe uma conta com este email',
      error_generic: 'Erro ao criar conta. Tente novamente.'
    }
  };

  const msgs = validationMessages[language] || validationMessages.fr;

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.dj_name.trim()) {
      newErrors.dj_name = msgs.dj_name_required;
    } else if (formData.dj_name.trim().length < 2) {
      newErrors.dj_name = msgs.dj_name_min;
    }
    
    if (!formData.email.trim()) {
      newErrors.email = msgs.email_required;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = msgs.email_invalid;
    }
    
    if (!formData.password) {
      newErrors.password = msgs.password_required;
    } else if (formData.password.length < 8) {
      newErrors.password = msgs.password_min;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    // Clear emailExists error when user changes email
    if (name === 'email' && emailExists) {
      setEmailExists(false);
    }
  };

  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailExists(false);
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // New flow: register returns message to check email
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        email: formData.email,
        password: formData.password,
        dj_name: formData.dj_name
      });
      
      if (response.data.ok) {
        setRegisteredEmail(formData.email);
        setRegistrationComplete(true);
        toast.success(t('auth', 'verification_sent'));
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      if (status === 409) {
        setEmailExists(true);
        setErrors(prev => ({ ...prev, email: detail || msgs.email_exists }));
      } else {
        toast.error(detail || msgs.error_generic);
      }
    } finally {
      setLoading(false);
    }
  };

  // Success message translations
  const successMessages = {
    fr: {
      title: 'Vérifie tes emails ! 📬',
      sent_to: 'Un email de vérification a été envoyé à :',
      instructions: 'Clique sur le lien dans l\'email pour activer ton compte et accéder à ton dashboard.',
      not_received: 'Pas reçu ? Vérifie tes spams ou',
      resend: 'renvoyer l\'email',
      back_to_login: 'Retour à la connexion'
    },
    en: {
      title: 'Check your emails! 📬',
      sent_to: 'A verification email has been sent to:',
      instructions: 'Click the link in the email to activate your account and access your dashboard.',
      not_received: 'Not received? Check your spam or',
      resend: 'resend email',
      back_to_login: 'Back to login'
    },
    es: {
      title: '¡Revisa tus emails! 📬',
      sent_to: 'Se ha enviado un email de verificación a:',
      instructions: 'Haz clic en el enlace del email para activar tu cuenta y acceder a tu panel.',
      not_received: '¿No lo recibiste? Revisa spam o',
      resend: 'reenviar email',
      back_to_login: 'Volver al inicio de sesión'
    },
    de: {
      title: 'Überprüfe deine E-Mails! 📬',
      sent_to: 'Eine Bestätigungs-E-Mail wurde gesendet an:',
      instructions: 'Klicke auf den Link in der E-Mail, um dein Konto zu aktivieren und auf dein Dashboard zuzugreifen.',
      not_received: 'Nicht erhalten? Überprüfe deinen Spam oder',
      resend: 'E-Mail erneut senden',
      back_to_login: 'Zurück zur Anmeldung'
    },
    pt: {
      title: 'Verifique seus emails! 📬',
      sent_to: 'Um email de verificação foi enviado para:',
      instructions: 'Clique no link do email para ativar sua conta e acessar seu painel.',
      not_received: 'Não recebeu? Verifique spam ou',
      resend: 'reenviar email',
      back_to_login: 'Voltar ao login'
    }
  };

  const successMsgs = successMessages[language] || successMessages.fr;

  // Show success screen after registration
  if (registrationComplete) {
    return (
      <div 
        className="min-h-screen bg-[#050505] flex items-center justify-center p-6"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(5,5,5,0.95), rgba(5,5,5,0.8)), url('https://images.unsplash.com/photo-1639323251610-d8218455e596?w=1200')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-neon-cyan/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-12 h-12 text-neon-cyan" />
          </div>
          <h2 className="font-heading text-2xl text-white mb-4">{successMsgs.title}</h2>
          <p className="text-gray-300 mb-2">
            {successMsgs.sent_to}
          </p>
          <p className="text-neon-cyan font-semibold mb-6">{registeredEmail}</p>
          <p className="text-gray-400 text-sm mb-6">
            {successMsgs.instructions}
          </p>
          <div className="bg-white/5 rounded-lg p-4 mb-6">
            <p className="text-gray-500 text-xs">
              💡 {successMsgs.not_received}{' '}
              <button 
                onClick={async () => {
                  try {
                    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-verification`, {
                      email: registeredEmail
                    });
                    toast.success(t('auth', 'verification_sent'));
                  } catch {
                    toast.error(t('common', 'error'));
                  }
                }}
                className="text-neon-cyan hover:underline"
              >
                {successMsgs.resend}
              </button>
            </p>
          </div>
          <Link to="/login">
            <Button variant="outline" className="border-white/20 text-gray-300 hover:bg-white/10 rounded-full">
              {successMsgs.back_to_login}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Welcome messages per language
  const welcomeMessages = {
    fr: {
      welcome: 'Bienvenue sur',
      tagline: 'L\'app pensée par des DJs, pour des DJs.',
      login_link: 'Se connecter',
      already_account: 'Déjà un compte ?',
      plan_info: 'Compte gratuit • Accès aux fonctionnalités essentielles',
      creating: 'Création en cours...'
    },
    en: {
      welcome: 'Welcome to',
      tagline: 'The app made by DJs, for DJs.',
      login_link: 'Log in',
      already_account: 'Already have an account?',
      plan_info: 'Free account • Access to essential features',
      creating: 'Creating...'
    },
    es: {
      welcome: 'Bienvenido a',
      tagline: 'La app creada por DJs, para DJs.',
      login_link: 'Iniciar sesión',
      already_account: '¿Ya tienes cuenta?',
      plan_info: 'Cuenta gratis • Acceso a funciones esenciales',
      creating: 'Creando...'
    },
    de: {
      welcome: 'Willkommen bei',
      tagline: 'Die App von DJs, für DJs.',
      login_link: 'Anmelden',
      already_account: 'Bereits ein Konto?',
      plan_info: 'Kostenloses Konto • Zugang zu wesentlichen Funktionen',
      creating: 'Wird erstellt...'
    },
    pt: {
      welcome: 'Bem-vindo ao',
      tagline: 'O app feito por DJs, para DJs.',
      login_link: 'Entrar',
      already_account: 'Já tem uma conta?',
      plan_info: 'Conta grátis • Acesso a recursos essenciais',
      creating: 'Criando...'
    }
  };

  const welcomeMsgs = welcomeMessages[language] || welcomeMessages.fr;

  return (
    <div 
      className="min-h-screen bg-[#050505] flex"
      style={{
        backgroundImage: `linear-gradient(to right, rgba(5,5,5,0.95), rgba(5,5,5,0.8)), url('https://images.unsplash.com/photo-1639323251610-d8218455e596?w=1200')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="glass rounded-2xl p-8 max-w-md w-full animate-fade-in-up">
          {/* Language Selector */}
          <div className="flex justify-end mb-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white">
                  <Globe className="w-4 h-4 mr-1" />
                  {LANGUAGE_FLAGS[language]}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1a1a1a] border-white/10">
                {['fr', 'en', 'es', 'de', 'pt'].map((lang) => (
                  <DropdownMenuItem 
                    key={lang}
                    onClick={() => setLanguage(lang)}
                    className="text-white hover:bg-white/10 cursor-pointer"
                  >
                    {LANGUAGE_FLAGS[lang]} {LANGUAGE_NAMES[lang]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* TrackVibes Logo & Tagline */}
          <div className="text-center mb-8">
            <img 
              src="/assets/trackvibes-logo.png" 
              alt="TrackVibes" 
              className="h-24 w-auto mx-auto mb-4"
            />
            <p className="text-gray-300 text-lg font-light">
              {welcomeMsgs.welcome} <span className="text-neon-cyan font-semibold">TrackVibes</span>
            </p>
            <p className="text-gray-500 text-sm mt-1">
              {welcomeMsgs.tagline}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* DJ Name */}
            <div className="space-y-1.5">
              <Label className="text-gray-300 flex items-center gap-2">
                <User className="w-4 h-4 text-neon-cyan" />
                {t('auth', 'dj_name')}
              </Label>
              <Input
                name="dj_name"
                value={formData.dj_name}
                onChange={handleChange}
                placeholder={t('auth', 'dj_name_placeholder')}
                className={`bg-white/5 border text-white placeholder:text-white/30 focus:border-neon-cyan h-12 ${
                  errors.dj_name ? 'border-red-500' : 'border-white/10'
                }`}
                disabled={loading}
                data-testid="register-djname-input"
              />
              {errors.dj_name && (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.dj_name}
                </p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label className="text-gray-300 flex items-center gap-2">
                <Mail className="w-4 h-4 text-neon-cyan" />
                {t('common', 'email')}
              </Label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('auth', 'email_placeholder')}
                className={`bg-white/5 border text-white placeholder:text-white/30 focus:border-neon-cyan h-12 ${
                  errors.email ? 'border-red-500' : 'border-white/10'
                }`}
                disabled={loading}
                data-testid="register-email-input"
              />
              {errors.email && (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.email}
                </p>
              )}
              {/* Email exists - show login link */}
              {emailExists && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mt-2">
                  <p className="text-amber-300 text-sm">
                    {msgs.email_exists}
                  </p>
                  <Link 
                    to="/login" 
                    className="text-neon-cyan hover:underline text-sm font-semibold mt-1 inline-block"
                  >
                    → {welcomeMsgs.login_link}
                  </Link>
                </div>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <Label className="text-gray-300 flex items-center gap-2">
                <Lock className="w-4 h-4 text-neon-cyan" />
                {t('common', 'password')}
              </Label>
              <div className="relative">
                <Input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className={`bg-white/5 border text-white placeholder:text-white/30 focus:border-neon-cyan h-12 pr-12 ${
                    errors.password ? 'border-red-500' : 'border-white/10'
                  }`}
                  disabled={loading}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-neon-cyan transition-colors p-1"
                  tabIndex={-1}
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password ? (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.password}
                </p>
              ) : (
                <p className="text-xs text-gray-500">{msgs.password_min}</p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-neon-cyan text-black font-bold uppercase tracking-widest hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] rounded-full py-6 text-lg btn-neon disabled:opacity-50"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {welcomeMsgs.creating}
                </span>
              ) : (
                <>
                  <UserPlus className="w-5 h-5 mr-2" />
                  {t('auth', 'register_button')}
                </>
              )}
            </Button>
          </form>

          {/* Login Link */}
          <div className="mt-8 text-center">
            <p className="text-gray-400">
              {welcomeMsgs.already_account}{' '}
              <Link 
                to="/login" 
                className="text-neon-cyan hover:underline font-semibold"
                data-testid="login-link"
              >
                {welcomeMsgs.login_link}
              </Link>
            </p>
          </div>
          
          {/* Plan info */}
          <div className="mt-6 text-center">
            <p className="text-gray-600 text-xs">
              {welcomeMsgs.plan_info}
            </p>
          </div>
          
          {/* Legal Links Footer */}
          <div className="mt-8 pt-6 border-t border-white/10">
            <nav className="flex justify-center gap-4 text-xs text-gray-500">
              <Link to="/privacy" className="hover:text-gray-300">{language === 'fr' ? 'Confidentialité' : 'Privacy'}</Link>
              <span className="text-gray-700">|</span>
              <Link to="/conditions" className="hover:text-gray-300">{language === 'fr' ? 'CGU' : 'Terms'}</Link>
              <span className="text-gray-700">|</span>
              <Link to="/mentions-legales" className="hover:text-gray-300">{language === 'fr' ? 'Mentions légales' : 'Legal'}</Link>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
